import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus, Loader2, Search, Video, FileText, Link2,
  Trash2, Edit, Eye, EyeOff, Filter, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeIcons = { video: Video, pdf: FileText, link: Link2 };
const typeLabels = { video: 'Video', pdf: 'PDF', link: 'Link' };

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}

export default function MediaLibraryPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '', category: '', media_type: 'link', url: '',
    patient_facing_title: '', language: 'English', concern_tags: ''
  });

  const isAdmin = user?.role === 'admin';

  const fetchItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (filterType !== 'all') params.set('media_type', filterType);
      if (filterCategory !== 'all') params.set('category', filterCategory);
      const { data } = await axios.get(`${API_URL}/api/media-library?${params}`, { withCredentials: true });
      setItems(data);
    } catch (error) {
      toast.error('Failed to fetch media library');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterType, filterCategory]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];

  const openCreate = () => {
    setEditingItem(null);
    setForm({ title: '', category: '', media_type: 'link', url: '', patient_facing_title: '', language: 'English', concern_tags: '' });
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title, category: item.category, media_type: item.media_type,
      url: item.url, patient_facing_title: item.patient_facing_title,
      language: item.language, concern_tags: item.concern_tags.join(', ')
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.url || !form.media_type) {
      toast.error('Title, URL, and type are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        concern_tags: form.concern_tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      if (editingItem) {
        await axios.put(`${API_URL}/api/media-library/${editingItem.id}`, payload, { withCredentials: true });
        toast.success('Media item updated');
      } else {
        await axios.post(`${API_URL}/api/media-library`, payload, { withCredentials: true });
        toast.success('Media item created');
      }
      setIsModalOpen(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this media item?')) return;
    try {
      await axios.delete(`${API_URL}/api/media-library/${id}`, { withCredentials: true });
      toast.success('Media item deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.put(`${API_URL}/api/media-library/${item.id}`, { is_active: !item.is_active }, { withCredentials: true });
      toast.success(item.is_active ? 'Deactivated' : 'Activated');
      fetchItems();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="media-library-loading"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="media-library-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">Media Library</h1>
          <p className="text-muted-foreground mt-1">Educational resources for patients</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-primary hover:bg-primary/90 rounded-full" data-testid="add-media-button">
            <Plus className="w-4 h-4 mr-2" />Add Media
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search media..." className="pl-9 rounded-xl" data-testid="media-search-input" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[140px] rounded-xl" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="video">Video</SelectItem>
            <SelectItem value="pdf">PDF</SelectItem>
            <SelectItem value="link">Link</SelectItem>
          </SelectContent>
        </Select>
        {categories.length > 0 && (
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[160px] rounded-xl" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Items Grid */}
      {items.length === 0 ? (
        <Card className="border-border shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Video className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No media items found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(item => {
            const TypeIcon = typeIcons[item.media_type] || Link2;
            return (
              <Card key={item.id} className={`border-border shadow-sm ${!item.is_active ? 'opacity-50' : ''}`} data-testid={`media-item-${item.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <TypeIcon className="w-5 h-5 text-primary" />
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">{typeLabels[item.media_type]}</Badge>
                      {item.language !== 'English' && <Badge variant="secondary" className="text-xs">{item.language}</Badge>}
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleToggleActive(item)} data-testid={`toggle-active-${item.id}`}>
                          {item.is_active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)} data-testid={`edit-media-${item.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-media-${item.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <h3 className="font-heading font-medium mb-1 line-clamp-1">{item.title}</h3>
                  {item.patient_facing_title && <p className="text-sm text-muted-foreground mb-2 line-clamp-1">{item.patient_facing_title}</p>}
                  {item.category && <p className="text-xs text-muted-foreground mb-2">{item.category}</p>}
                  {item.concern_tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.concern_tags.map((tag, i) => <Badge key={i} variant="secondary" className="text-xs capitalize">{tag}</Badge>)}
                    </div>
                  )}
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-primary hover:underline">
                    <ExternalLink className="w-3 h-3" />Open resource
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="media-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingItem ? 'Edit Media Item' : 'Add Media Item'}</DialogTitle>
            <DialogDescription>Add educational resources for patients</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Resource title" className="rounded-xl" data-testid="media-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={form.media_type} onValueChange={(val) => setForm(p => ({ ...p, media_type: val }))}>
                  <SelectTrigger className="rounded-xl" data-testid="media-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="link">Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Language</Label>
                <Input value={form.language} onChange={(e) => setForm(p => ({ ...p, language: e.target.value }))} placeholder="English" className="rounded-xl" data-testid="media-language-input" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>URL *</Label>
                <Input value={form.url} onChange={(e) => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://..." className="rounded-xl" data-testid="media-url-input" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Patient-Facing Title</Label>
                <Input value={form.patient_facing_title} onChange={(e) => setForm(p => ({ ...p, patient_facing_title: e.target.value }))} placeholder="How patients will see this" className="rounded-xl" data-testid="media-patient-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g., Skincare, Acne" className="rounded-xl" data-testid="media-category-input" />
              </div>
              <div className="space-y-2">
                <Label>Concern Tags</Label>
                <Input value={form.concern_tags} onChange={(e) => setForm(p => ({ ...p, concern_tags: e.target.value }))} placeholder="acne, pigmentation" className="rounded-xl" data-testid="media-tags-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90 rounded-full" data-testid="media-submit-button">
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
